import dayjs from 'dayjs'
import Big from 'big.js'
import {v4 as uuidv4} from 'uuid'
import {cloneDeep} from 'lodash'
import {
  isSameDay,
  // differenceInCalendarMonths, addMonths, getDate, setDate,
} from 'date-fns'
import {roundNumber, reduceArrayNumber} from 'common/helpers'
import {ForecastFormItem, ForecastItem, ForecastType} from 'common/types'

export const getInitialForecast: (
  totalAmount: number,
  openDate: Date,
  maturityDate: Date,
  initDDAmount?: number
) => null | ForecastFormItem[] = (totalAmount, openDate, maturityDate, initDDAmount) => {
  const items: ForecastFormItem[] = []

  const openDay     = dayjs(openDate)
  const maturityDay = dayjs(maturityDate)
  if (
    !openDay.isValid() ||
    !maturityDay.isValid() ||
    (
      (!!initDDAmount) &&
      (initDDAmount !== 0) &&
      (initDDAmount > totalAmount)
    )
  ) {
    console.error('Data input error!')
    return null
  }
  for (
    let ddDay = openDay.startOf('month').add(20-1, 'day');
    ddDay.isBefore(maturityDay) || ddDay.isSame(maturityDay, 'day');
    ddDay = ddDay.add(1, 'month')
  ) {
    if (openDay.isBefore(ddDay)) {
      items.push({
        uuid: uuidv4(),
        date: ddDay.toDate(),
        type: 'drawdown' as ForecastType,
        amount: 0,
        initialDrawdown: false,
      })
    }
  }

  // console.table([...items])
  if (
    !initDDAmount &&
    (initDDAmount !== 0)
  ) {
    // initialDDAmount is undefined (in case of normal loan request)
    items.unshift({
      uuid: uuidv4(),
      date: openDay.toDate(),
      type: 'drawdown' as ForecastType,
      amount: 0,
      initialDrawdown: true,
    })
  }

  const count = items.length
  if (count === 0) return null
  const totalReloAmount = Big(totalAmount).sub(initDDAmount || 0)
  const amount = totalReloAmount.div(count).round(2, Big.roundHalfUp).toNumber()
  const subTotal = Big(amount).times(count).toNumber()
  const rest = totalReloAmount.minus(subTotal).toNumber()
  items.forEach((item:ForecastFormItem) => { item.amount = amount })
  if (!!rest) {
    const last = items.at(-1)
    last.amount = Big(last.amount).add(rest).toNumber()
  }

  if (
    !!initDDAmount &&
    (initDDAmount !== 0)
   ) {
    items.unshift({
      uuid: uuidv4(),
      date: openDay.toDate(),
      type: 'drawdown' as ForecastType,
      amount: initDDAmount,
      initialDrawdown: true,
    })
  }

  return items
}

type IsItemType = (item: ForecastItem | ForecastFormItem) => boolean
export const isItemDrawdown = (item:any):boolean => ( 
  (!!item?.type) && ( item.type.indexOf('drawdown') > -1 )  
)
export const isItemPureDrawdown = (item:any):boolean => ( 
  (!!item?.type) && ( item.type.indexOf('drawdown') > -1 ) 
)
export const isItemInDrawdownColumn = (item:any):boolean => ( 
  (!!item?.type) && (
    ( item.type.indexOf('drawdown') > -1 ) ||
    ['legal-fee', 'broker-fee', 'other-fee', 'gst'].includes(item.type)
  ) 
)

export const isItemRepayment: IsItemType = (item) => item.type === 'repayment'

export const isEstimatedForecastItem = (item: ForecastItem): boolean =>
  !item.isFake && !item.transactions?.length

export const isTransactedForecastItem = (item: ForecastItem): boolean =>
  Boolean(item.transactions?.length || item.isFake)

export const getNextEstimatedDrawdowns = (
  drawdowns: ForecastFormItem[],
  date: Date,
  uuid?: string
) => {
  let currentIndex
  if (uuid) {
    currentIndex = drawdowns.findIndex((drawdown) => drawdown.uuid === uuid)
  }
  const nextDrawdowns = uuid
    ? drawdowns.slice(currentIndex + 1).filter((item) => !item.transactions)
    : drawdowns.filter(
        (item) => item.uuid !== uuid && item.date > date && !item.transactions
      )
  return nextDrawdowns
}

export const getPrevHappenedDrawdowns = (
  drawdowns: ForecastFormItem[],
  date: Date,
  uuid?: string
) => {
  let currentIndex: number
  if (uuid) {
    currentIndex = drawdowns.findIndex((drawdown) => drawdown.uuid === uuid)
  }
  return drawdowns.filter(
    (item, index) =>
      ((!uuid || (uuid && currentIndex > index)) && item.date <= date) ||
      item.transactions
  )
}

export const getNextDrawdownsLimitAmount = (
  loanValue: number,
  amountValue: number,
  prevDrawdownsAmount: number
) => roundNumber(loanValue - amountValue - prevDrawdownsAmount)

export const getDrawdownsSurplus = (
  drawdowns: ForecastFormItem[],
  date: Date,
  loanValue: number,
  amountValue: number = 0,
  forecatItemUuid?: string
): number => {
  let nextEstimatedDrawdowns = getNextEstimatedDrawdowns(
    drawdowns,
    date,
    forecatItemUuid
  )
  if (!nextEstimatedDrawdowns.length) return 0
  const prevHappenedDrawdowns = getPrevHappenedDrawdowns(
    drawdowns,
    date,
    forecatItemUuid
  )
  const nextDrawdownsLimitAmount = getNextDrawdownsLimitAmount(
    loanValue,
    amountValue,
    reduceArrayNumber(prevHappenedDrawdowns, 'amount')
  )
  return roundNumber(
    nextDrawdownsLimitAmount -
      reduceArrayNumber(nextEstimatedDrawdowns, 'amount')
  )
}

export const isForecastSurplusAllocate = (
  isForecastEditByDrawdown: boolean,
  surplus: number
): boolean => isForecastEditByDrawdown && surplus > 0

export const getNewForecastItemFormValues = (
  values: any,
  forecastItem: ForecastFormItem
): ForecastFormItem => ({
  ...values,
  uuid: forecastItem ? forecastItem.uuid : uuidv4(),
})

export const getNewSortedForecastByChangeItem = (
  forecastItem: ForecastFormItem,
  forecast: ForecastFormItem[]
): ForecastFormItem[] => {
  // new item
  if (forecast.every((item) => forecastItem.uuid !== item.uuid)) {
    const newItemIndex = forecast.findIndex(
      (item) => forecastItem.date < item.date
    )
    const newForecast = forecast.slice()
    newForecast.splice(
      newItemIndex === -1 ? newForecast.length : newItemIndex,
      0,
      forecastItem
    )
    return newForecast
  }
  // edit item (same date)
  const currentItem = forecast.find((item) => forecastItem.uuid === item.uuid)
  if (isSameDay(forecastItem.date, currentItem.date)) {
    const currentIndex = forecast.findIndex(
      (item) => item.uuid === forecastItem.uuid
    )
    const newForecast = forecast.slice()
    newForecast[currentIndex] = {...forecastItem}
    return newForecast
  }
  // edit item (diff date)
  const newForecast = forecast.filter((item) => forecastItem.uuid !== item.uuid)
  const newItemIndex = forecast.findIndex(
    (item) => forecastItem.date < item.date
  )
  newForecast.splice(
    newItemIndex === -1 ? newForecast.length : newItemIndex,
    0,
    forecastItem
  )
  return newForecast
}

interface ChangeForecastDrawdownItemParams {
  forecast: any[]
  drawdown: ForecastFormItem
  loanValue: number
  hasForecast?: boolean
}
export const changeForecastDrawdownItem = ({
  forecast,
  drawdown,
  loanValue,
  hasForecast = false,
}: ChangeForecastDrawdownItemParams): any[] => {
  let newForecast = cloneDeep(forecast)
  const drawdowns = newForecast.filter((item) => isItemDrawdown(item))
  let nextEstimatedDrawdowns = getNextEstimatedDrawdowns(
    drawdowns,
    drawdown.date,
    drawdown.uuid
  )
  if (!nextEstimatedDrawdowns.length) return newForecast
  const prevHappenedDrawdowns = getPrevHappenedDrawdowns(
    drawdowns,
    drawdown.date,
    drawdown.uuid
  )
  const nextDrawdownsLimitAmount = getNextDrawdownsLimitAmount(
    loanValue,
    drawdown.amount,
    reduceArrayNumber(prevHappenedDrawdowns, 'amount')
  )
  if (!hasForecast && nextDrawdownsLimitAmount < 0) {
    newForecast.forEach((item) => {
      if (
        !nextEstimatedDrawdowns.some((nextItem) => nextItem.uuid === item.uuid)
      ) {
        return
      }
      item.amount = 0
    })
    return newForecast
  }
  let totalNextEstimatedDrawdownsAmount = reduceArrayNumber(
    nextEstimatedDrawdowns,
    'amount'
  )
  const surplus = (
    Big(nextDrawdownsLimitAmount).sub(totalNextEstimatedDrawdownsAmount).toNumber()
  )

  // console.table(prevHappenedDrawdowns)
  // console.table(nextEstimatedDrawdowns)
  // console.table([{
  //   loanValue, ddAmount: drawdown.amount, 
  //   sumPrev: reduceArrayNumber(prevHappenedDrawdowns, 'amount')
  // }])
  // console.table([{
  //   nextDrawdownsLimitAmount, totalNextEstimatedDrawdownsAmount,surplus
  // }])
  // console.table(newForecast.map((x) => ({...x})))

  const initDDofNext = nextEstimatedDrawdowns?.find((x:any) => !!x.initialDrawdown)
  // console.log(initDDofNext)
  if (!!initDDofNext) {
    totalNextEstimatedDrawdownsAmount = Big(totalNextEstimatedDrawdownsAmount).sub(initDDofNext.amount).toNumber()
  }
  const surplusDivider = !!initDDofNext ? (nextEstimatedDrawdowns.length - 1) : nextEstimatedDrawdowns.length
  newForecast.forEach((item) => {
    if (
      !nextEstimatedDrawdowns.some((nextItem) => nextItem.uuid === item.uuid)
    ) {
      return
    }
    if (!!item.initialDrawdown) return
    if (surplusDivider < 1) return

    item.amount = roundNumber(
      totalNextEstimatedDrawdownsAmount
        ? item.amount +
            surplus * (item.amount / totalNextEstimatedDrawdownsAmount)
        : item.amount + surplus / surplusDivider
    )
  })
  // console.table(newForecast)

  if (hasForecast) {
    newForecast = newForecast.filter(
      (item) =>
        nextEstimatedDrawdowns.some(
          (nextItem) => nextItem.uuid === item.uuid
        ) || item.uuid === drawdown.uuid
    )
  }
  const drawdownsTotalAmount = reduceArrayNumber(drawdowns, 'amount')
  if (drawdownsTotalAmount !== loanValue) {
    const lastDrawdown = nextEstimatedDrawdowns.at(-1)
    newForecast.find((item) => item.uuid === lastDrawdown.uuid).amount =
      roundNumber(lastDrawdown.amount - (drawdownsTotalAmount - loanValue))
  }
  return newForecast
}
